import { listAccounts } from '../../../api/accounts'
import { listLegalEntities } from '../../../api/legalEntities'
import { listPrograms } from '../../../api/cartridgeStudio'
import { rsqlContains } from '../../../lib/rsql'
import { label } from '../../../lib/labels'
import type { TypeaheadOption } from './TypeaheadPicker'

const SEARCH_LIMIT = 10

/**
 * Typeahead sources for the dashboard filter pickers (IB-311 Slice 1) — every one wraps an
 * **already-published** list endpoint (`GET /accounts`, `GET /legal-entities`, `GET /programs`); no
 * new backend route this slice. Each degrades to an empty result list on any failure (network,
 * 403/`not_authorized` if the signed-in user's capability set can't reach that particular list,
 * etc.) rather than throwing into the picker — `TypeaheadPicker` then honestly shows "No matches"
 * for that state. This is a disclosed simplification: a true permission denial and a genuine
 * zero-result search render identically here; distinguishing them would need `TypeaheadPicker` to
 * carry a third state, left for a follow-up polish pass rather than done here to keep this slice
 * frontend-only and additive.
 */

export async function searchPrograms(term: string, signal: AbortSignal): Promise<TypeaheadOption[]> {
  try {
    const page = await listPrograms({ filter: rsqlContains('code', term), limit: SEARCH_LIMIT }, signal)
    return page.items.map((p) => ({
      value: p.id,
      label: p.code,
      secondary: `${label('jurisdiction', p.jurisdiction)} · ${p.kind}`,
    }))
  } catch {
    return []
  }
}

export async function searchAccounts(term: string, signal: AbortSignal): Promise<TypeaheadOption[]> {
  try {
    const page = await listAccounts({ filter: rsqlContains('name', term), limit: SEARCH_LIMIT }, signal)
    return page.items.map((a) => ({ value: a.id, label: a.name, secondary: a.type }))
  } catch {
    return []
  }
}

export async function searchLegalEntities(term: string, signal: AbortSignal): Promise<TypeaheadOption[]> {
  try {
    const page = await listLegalEntities({ filter: rsqlContains('name', term), limit: SEARCH_LIMIT }, signal)
    return page.items.map((e) => ({ value: e.id, label: e.name, secondary: e.accountName ?? undefined }))
  } catch {
    return []
  }
}
